<script lang="ts">
    import { urlsBuilderContextToken } from "@/context_tokens";
    import AddProductToWishlistButton from "@apps/iloom_customization/components/AddProductToWishlistButton.svelte";
    import { ProductQuery } from "@apps/iloom_customization/modules/products/queries/ProductQuery";
    import { CustomReviewEntity } from "@apps/iloom_customization/reviews/stores/ReviewsStore";
    import { WishlistProductStatusesQuery } from "@apps/wishlist/queries/WishlistProductStatusesQuery";
    import { WishlistsService } from "@apps/wishlist/services/WishlistsService";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import cn from "classnames";
    import { combineLatest, fromEvent, of } from "rxjs";
    import { map, scan, startWith, switchMap, takeUntil } from "rxjs/operators";
    import { createEventDispatcher, onMount } from "svelte";

    import Image from "@components/Image/Image.svelte";
    import Money from "@components/Money.svelte";
    import Slider from "@components/Slider/Slider.svelte";
    import SchemaSliderController from "@components/Slider/SliderController";

    import ReviewsMultiplePhotoIcon from "./icons/ReviewsMultiplePhotoIcon.svelte";
    import ReviewsItemFrame from "./ReviewsItemFrame.svelte";
    import ReviewsRatingStars from "./ReviewsRatingStars.svelte";

    export let isFeatured = false;
    export let productQuery: ProductQuery;
    export let review: CustomReviewEntity;
    export let wishlistsService: WishlistsService;
    export let wishlistProductStatusesQuery: WishlistProductStatusesQuery;

    const urlsBuilder = getTypedContext(urlsBuilderContextToken);

    const gqlProducts$ = productQuery.all$;
    const product$ = review.product?.id
        ? productQuery.selectProduct(review.product.id)
        : of(null);
    $: firstVariantOfProduct = $product$?.variants[0];

    const isMobile = window.innerWidth <= 600;

    const sliderController = new SchemaSliderController();

    const dispatch = createEventDispatcher();

    const handleOpenReview = () =>
        dispatch("openModal", { reviewId: review.id });

    $: tags = $gqlProducts$
        .find((gqlProduct) => gqlProduct.id === review.product?.id)
        ?.tags.filter((tag) => tag.toLocaleLowerCase().includes("series"))
        .map((tag) => tag.replace("series: ", ""));

    let reviewItemElement: HTMLElement;

    onMount(() => {
        if (reviewItemElement) {
            const mouseDown$ = fromEvent<MouseEvent>(
                reviewItemElement,
                "mousedown",
                { capture: true }
            );
            const mouseUp$ = fromEvent<MouseEvent>(
                reviewItemElement,
                "mouseup",
                { capture: true }
            );
            const mouseMove$ = fromEvent<MouseEvent>(
                reviewItemElement,
                "mousemove",
                { capture: true }
            );
            const swipe$ = mouseMove$.pipe(
                scan(
                    (acc, value) => {
                        const swipePosition: typeof acc = { ...acc };
                        if (!acc.initialX || !acc.initialY) {
                            swipePosition.initialX = value.screenX;
                            swipePosition.initialY = value.screenY;
                        }
                        swipePosition.deltaX = Math.abs(
                            acc.initialX - value.screenX
                        );
                        swipePosition.deltaY = Math.abs(
                            acc.initialY - value.screenY
                        );
                        return swipePosition;
                    },
                    {
                        deltaX: 0,
                        deltaY: 0,
                        initialX: 0,
                        initialY: 0,
                    }
                ),
                map((value) => {
                    if (value.deltaX > 5 || value.deltaY > 5) {
                        return true;
                    }
                    return false;
                }),
                startWith(false),
                takeUntil(mouseUp$)
            );
            const clickOrSwipe$ = mouseDown$.pipe(
                switchMap(() => {
                    return combineLatest(swipe$, mouseUp$);
                }),
                map(([swipe, mouseUp]) => {
                    if (swipe && mouseUp) {
                        return false;
                    }
                    return true;
                })
            );
            const swipeSubscription = clickOrSwipe$.subscribe((isClick) => {
                if (isClick) {
                    handleOpenReview();
                }
            });
            return () => {
                swipeSubscription.unsubscribe();
            };
        }
    });
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<ReviewsItemFrame {isFeatured}>
    <svelte:fragment slot="media">
        {#if review.images.length}
            <div class="gw-crv-item__slider" bind:this={reviewItemElement}>
                <Slider
                    draggable={!isMobile}
                    {sliderController}
                    perPage={1}
                    loop={false}
                >
                    <svelte:fragment slot="slides">
                        {#each review.images as image, index (image.url + index)}
                            <div
                                class={cn({
                                    "gw-crv-item__image-rounded": !isFeatured,
                                })}
                            >
                                <Image
                                    ratio={1 / 1}
                                    alt={review.title || ""}
                                    src={image.url}
                                    objectFit="cover"
                                />
                            </div>
                        {/each}
                    </svelte:fragment>
                </Slider>
                {#if review.images.length > 1}
                    <ReviewsMultiplePhotoIcon />
                {/if}
            </div>
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="rating">
        {#if isFeatured}
            <ReviewsRatingStars rating={review.rating} />
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="title">
        {#if review.title}
            <h3
                class={cn("gw-crv-item__title", {
                    ["gw-crv-item__title-featured"]: isFeatured,
                })}
            >
                {review.title}
            </h3>
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="text">
        <p class="gw-crv-item__body">{review.body}</p>
    </svelte:fragment>

    <svelte:fragment slot="product-image">
        {#if $product$?.featuredImage}
            <a
                href={urlsBuilder.createProductUrl($product$.handle)}
                class="gw-crv-item-product__link"
                aria-label={$product$.title || "Browse product"}
            >
                <Image
                    src={urlsBuilder.createProductImageUrl(
                        $product$.featuredImage.url,
                        { width: 128 }
                    )}
                    alt={$product$.featuredImage.altText ||
                        $product$.title + " image"}
                    blur
                    objectFit="contain"
                />
            </a>
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="product-tags">
        {#if tags?.length}
            <div class="gw-crv-item-product__tags">
                {#each tags as tag}
                    <span>{tag}</span>
                {/each}
            </div>
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="product-title">
        {#if $product$}
            <a
                href={urlsBuilder.createProductUrl($product$.handle)}
                class="gw-crv-item-product__link gw-crv-item-product__title"
            >
                {$product$.title}
            </a>
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="product-price">
        {#if firstVariantOfProduct}
            <span class="gw-crv-item-product__price">
                <Money
                    with_currency
                    amount={firstVariantOfProduct.price.amount}
                />
            </span>
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="product-wishlist-btn">
        {#if $product$ && $product$.variants[0].id}
            <AddProductToWishlistButton
                variantId={$product$.variants[0].id}
                productId={$product$.id}
                {wishlistsService}
                {wishlistProductStatusesQuery}
            />
        {/if}
    </svelte:fragment>
</ReviewsItemFrame>

<style lang="postcss">
    .gw-crv-item {
        &__slider {
            position: relative;
            :global(svg) {
                position: absolute;
                top: 12px;
                right: 12px;
            }
        }
        &__image-rounded {
            border-radius: 10px;
            overflow: hidden;
        }

        &__title {
            font-family: "Noto Sans TC";
            font-weight: 700;
            font-size: 22px;
            line-height: 31px;
            color: #3c3c3c;
            margin: 0 0 18px;
            word-wrap: break-word;
        }
        &__body {
            color: #666666;
            font-family: "Noto Sans TC";
            font-weight: 400;
            font-size: 16px;
            line-height: 26px;
            margin: 0;
            height: 78px;
            overflow: hidden;
            text-overflow: ellipsis;
            display: block;
            display: -webkit-box;
            display: -moz-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            -moz-line-clamp: 3;
        }

        &-product {
            &__tags {
                font-size: 16px;
                font-family: "Montserrat";
                font-weight: 400;
                line-height: 1.25;
                display: flex;
                overflow: scroll;
                white-space: nowrap;
                flex-wrap: nowrap;
                gap: 6px;
                color: #3c3c3c;
                margin-bottom: 0.5em;
                &::-webkit-scrollbar {
                    display: none;
                }
            }
            &__link {
                text-decoration: none;
            }

            &__title {
                display: block;
                font-family: "Noto Sans TC";
                font-size: 16px;
                line-height: 24px;
                font-weight: 700;
                margin: 0 0 2px;
                color: #3c3c3c;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            &__price {
                color: #3c3c3c;
                font-family: "Avenir";
                font-weight: 400;
                font-size: 18px;
                line-height: 24px;
            }
        }
    }

    @media all and (max-width: 600px) {
        .gw-crv-item {
            &__title {
                font-size: 12px;
                line-height: 20px;
                margin: 0 0 6px;
                &-featured {
                    font-size: 14px;
                }
            }
            &__body {
                height: 60px;
                font-size: 12px;
                line-height: 20px;
            }

            &-product {
                &__tags {
                    font-size: 12px;
                }
                &__title {
                    font-size: 12px;
                    line-height: 18px;
                }

                &__price {
                    font-size: 12px;
                    line-height: 14px;
                }
            }
        }
    }
</style>
